import { z } from 'zod';
import { NeedEffects, type NeedKey } from './play';

// Businesses, as Lagos Life's Company: a player starts a business of a kind (a café, a barber…) in an area of
// the map, in premises of a size they rent by the week or buy outright. City customers come every evening at
// 6 pm London time and the takings go into the business's till; on Saturday at 6 pm the week's bills come out
// of the till (rent if rented, the staff's wages, tax on the week's takings). Rent that cannot be paid has a few
// days' grace and then the doors shut; wages that cannot be paid make staff leave. The owner cashes the till out
// to their wallet, hires and lets staff go, picks cheap, fair or premium prices, and can sell the business back.
// Everything here is content the admins edit (kinds, sizes, areas, prices and rules).

// ---- what the admins edit

export const BusinessKind = z.object({
  id: z.string().regex(/^[a-z0-9_]{2,30}$/),
  label: z.string().trim().min(2).max(40),
  icon: z.string().trim().min(1).max(16),
  blurb: z.string().trim().max(120),
  // fitting it out, for a stall in an ordinary area (bigger premises and dearer areas cost more)
  setupPence: z.number().int().min(100).max(100_000_000),
  // what a customer spends, and the share of it that is profit
  ticketPence: z.number().int().min(10).max(10_000_000),
  margin: z.number().min(0.01).max(1),
  // customers on an ordinary evening at a stall with the usual staff
  customers: z.number().min(0.1).max(10_000),
  // people working there at a stall: the fewest it opens with, the usual, the most
  staff: z.object({
    min: z.number().int().min(0).max(50),
    usual: z.number().int().min(1).max(50),
    max: z.number().int().min(1).max(100),
  }),
  // a week's wage for one of them
  wagePence: z.number().int().min(0).max(10_000_000),
  // a week's rent as a share of the fit-out (for a stall in an ordinary area)
  rentShare: z.number().min(0.001).max(1),
  // how busy each day is, Monday to Sunday (1 is an ordinary day)
  days: z.array(z.number().min(0).max(5)).length(7),
  // how much one evening can swing (0.8 to 1.2: up to a fifth either way)
  swing: z.tuple([z.number().min(0).max(1), z.number().min(1).max(5)]),
  visible: z.boolean(),
});
export type BusinessKind = z.infer<typeof BusinessKind>;

export const PREMISES_KEYS = ['stall', 'shop', 'large'] as const;
export type PremisesKey = (typeof PREMISES_KEYS)[number];
export const Premises = z.object({
  label: z.string().trim().min(2).max(30),
  icon: z.string().trim().min(1).max(16),
  // compared with a stall: customers, rent, fit-out and staff
  customers: z.number().min(0.1).max(100),
  rent: z.number().min(0.1).max(100),
  setup: z.number().min(0.1).max(100),
  staff: z.number().min(1).max(20),
  // the most one evening can take
  capPence: z.number().int().min(100).max(1_000_000_000),
  // what the storeroom holds for the shelf (players' shops)
  stock: z.number().int().min(1).max(100_000).optional(),
});
export type Premises = z.infer<typeof Premises>;

export const BusinessArea = z.object({
  id: z.string().regex(/^[a-z0-9_]{2,30}$/),
  name: z.string().trim().min(2).max(40),
  // 1 (cheapest) to 4 (dearest)
  tier: z.number().int().min(1).max(4),
});
export type BusinessArea = z.infer<typeof BusinessArea>;
// what an area's tier does, compared with an ordinary area (tier 2)
export const AreaTier = z.object({
  rent: z.number().min(0.1).max(20),
  setup: z.number().min(0.1).max(20),
  customers: z.number().min(0.1).max(20),
  ticket: z.number().min(0.1).max(20),
  wage: z.number().min(0.1).max(20),
});
export type AreaTier = z.infer<typeof AreaTier>;

export const PRICE_KEYS = ['cheap', 'fair', 'premium'] as const;
export type PriceKey = (typeof PRICE_KEYS)[number];
export const PRICE_CHOICES: Record<PriceKey, { label: string; icon: string; customers: number; ticket: number }> = {
  cheap: { label: 'Cheap', icon: '🏷️', customers: 1.25, ticket: 0.8 },
  fair: { label: 'Fair', icon: '⚖️', customers: 1, ticket: 1 },
  premium: { label: 'Premium', icon: '💎', customers: 0.75, ticket: 1.35 },
};

// what part of a character's look a product changes (the character options' keys)
export const WEAR_PARTS = ['hair', 'hairColour', 'facialHair', 'outfit'] as const;
export type WearPart = (typeof WEAR_PARTS)[number];
export const WEAR_PART_LABELS: Record<WearPart, string> = {
  hair: 'Hairstyle',
  hairColour: 'Hair colour',
  facialHair: 'Facial hair',
  outfit: 'Outfit',
};

// What a shop's shelf can sell to other players (as Lagos Life's player shops): each product says which kinds of
// business may stock it, its usual price (owners price it within a band around it), and what it does for the
// one who buys it.
export const ShopProduct = z.object({
  id: z.string().regex(/^[a-z0-9_]{2,30}$/),
  label: z.string().trim().min(2).max(40),
  icon: z.string().trim().min(1).max(16),
  kinds: z.array(z.string().max(30)).min(1).max(30),
  refPence: z.number().int().min(10).max(10_000_000),
  effects: NeedEffects,
  // a look the buyer takes on (a haircut, a beard trim, an outfit): one at a time
  wear: z.object({ part: z.enum(WEAR_PARTS), value: z.string().min(1).max(40) }).optional(),
  // medicine (health.ts): treats mild sickness
  cures: z.literal('mild').optional(),
  visible: z.boolean(),
});
export type ShopProduct = z.infer<typeof ShopProduct>;
export const ShelfRules = z.object({
  // products on one shelf
  maxListed: z.number().int().min(1).max(20),
  // owners price within this band of the usual price
  bandMin: z.number().min(0.1).max(1),
  bandMax: z.number().min(1).max(10),
  // a buyer takes at most this many of one product at a time
  maxQty: z.number().int().min(1).max(50),
  // what one shop sells, and one buyer spends in shops, in a London day
  perShopDayPence: z.number().int().min(100).max(1_000_000_000),
  perBuyerDayPence: z.number().int().min(100).max(1_000_000_000),
  // days a life must be before it can buy from players' shops
  buyerAgeDays: z.number().int().min(0).max(30),
});
export type ShelfRules = z.infer<typeof ShelfRules>;
const product = (
  id: string,
  label: string,
  icon: string,
  kinds: string[],
  refPence: number,
  effects: Partial<Record<NeedKey, number>>,
  wear?: ShopProduct['wear'],
): ShopProduct => ({ id, label, icon, kinds, refPence, effects, ...(wear ? { wear } : {}), visible: true });
const look = (part: WearPart, value: string) => ({ part, value });
export const DEFAULT_PRODUCTS: ShopProduct[] = [
  product('jerk_chicken', 'Jerk chicken & rice', '🍗', ['food_stall'], 900, { hunger: 70, fun: 8 }),
  product('doner_wrap', 'Doner wrap', '🌯', ['food_stall'], 750, { hunger: 55, fun: 6 }),
  product('jollof_plantain', 'Jollof & plantain', '🍛', ['food_stall'], 850, { hunger: 65, fun: 10 }),
  product('patty_drink', 'Patty & a drink', '🥟', ['food_stall', 'corner_shop'], 400, { hunger: 30, fun: 4 }),
  product('flat_white', 'Flat white', '☕', ['cafe'], 350, { energy: 15, fun: 5 }),
  product('croissant', 'Almond croissant', '🥐', ['cafe'], 300, { hunger: 20, fun: 6 }),
  product('bacon_roll', 'Bacon roll', '🥪', ['cafe'], 450, { hunger: 35, fun: 4 }),
  product('smoothie', 'Berry smoothie', '🥤', ['cafe'], 500, { energy: 10, hunger: 15 }),
  product('meal_deal', 'Meal deal', '🥪', ['corner_shop'], 500, { hunger: 40, fun: 3 }),
  product('energy_drink', 'Energy drink', '⚡', ['corner_shop', 'pharmacy'], 200, { energy: 18, fun: 3 }),
  product('crisps_fizz', 'Crisps & a fizzy drink', '🍟', ['corner_shop'], 250, { hunger: 15, fun: 6 }),
  product('toiletries', 'Shower gel & toiletries', '🧴', ['corner_shop', 'pharmacy'], 600, { hygiene: 35 }),
  product('vitamins', 'Multivitamins', '💊', ['pharmacy'], 800, { energy: 22 }),
  { ...product('cold_flu', 'Cold & flu remedy', '🍯', ['pharmacy'], 650, { energy: 12, fun: 4 }), cures: 'mild' },
  // the barber's chair: a fresh trim, or a new style (the buyer wears it from then on)
  product('fresh_trim', 'Fresh trim', '💈', ['barber'], 1_500, { hygiene: 20, fun: 10 }),
  product('cut_waves', 'Waves', '💇', ['barber'], 1_800, { fun: 10 }, look('hair', 'waves')),
  product('cut_buzz', 'Buzz cut', '💇', ['barber'], 1_500, { fun: 8 }, look('hair', 'buzz_cut')),
  product('box_braids', 'Box braids', '💇', ['barber'], 3_500, { fun: 14 }, look('hair', 'box_braids')),
  product('cornrows', 'Cornrows', '💇', ['barber'], 2_500, { fun: 12 }, look('hair', 'cornrows')),
  product('locs', 'Locs', '💇', ['barber'], 4_000, { fun: 14 }, look('hair', 'locs')),
  product(
    'beard_trim',
    'Beard shape-up',
    '🧔',
    ['barber'],
    1_200,
    { hygiene: 8, fun: 6 },
    look('facialHair', 'short_beard'),
  ),
  // the boutique's rails: an outfit to wear
  product('fit_casual', 'Casual fit', '👕', ['boutique'], 3_500, { fun: 8 }, look('outfit', 'casual')),
  product('fit_smart', 'Smart casual', '👔', ['boutique'], 4_500, { fun: 10 }, look('outfit', 'smart_casual')),
  product('fit_office', 'Office suit', '🕴️', ['boutique'], 5_500, { fun: 8 }, look('outfit', 'office')),
  product('fit_sunday', 'Sunday best', '🎩', ['boutique'], 6_000, { fun: 12 }, look('outfit', 'sunday_best')),
  product('fit_ankara', 'Ankara dress', '👗', ['boutique'], 5_500, { fun: 12 }, look('outfit', 'ankara_dress')),
  product('fit_kaftan', 'Kaftan', '🥻', ['boutique'], 5_000, { fun: 10 }, look('outfit', 'kaftan')),
];
export const DEFAULT_SHELF_RULES: ShelfRules = {
  maxListed: 6,
  bandMin: 0.8,
  bandMax: 2.5,
  maxQty: 5,
  perShopDayPence: 1_000_000,
  perBuyerDayPence: 500_000,
  buyerAgeDays: 3,
};
// what a storeroom holds when the content does not say
export const DEFAULT_STOCK: Record<PremisesKey, number> = { stall: 60, shop: 250, large: 1_000 };
export const stockRoom = (content: BusinessContent, size: PremisesKey) =>
  content.premises[size].stock ?? DEFAULT_STOCK[size];
export const productsOf = (content: BusinessContent) => content.products ?? DEFAULT_PRODUCTS;
export const shelfRulesOf = (content: BusinessContent) => content.shelf ?? DEFAULT_SHELF_RULES;
// what the owner pays for one in stock: the usual price less the kind's profit, to the 5p
export const stockCostPence = (product: ShopProduct, kind: BusinessKind) =>
  Math.max(5, Math.round((product.refPence * (1 - kind.margin)) / 5) * 5);
// the prices an owner may ask
export const priceBand = (product: ShopProduct, rules: ShelfRules) => ({
  min: Math.ceil(product.refPence * rules.bandMin),
  max: Math.floor(product.refPence * rules.bandMax),
});

export const BusinessRules = z.object({
  open: z.boolean(),
  // registering a business (on top of the fit-out)
  registerPence: z.number().int().min(0).max(100_000_000),
  maxBusinesses: z.number().int().min(1).max(10),
  // buying the premises costs this many weeks of its rent
  buyWeeks: z.number().int().min(1).max(520),
  // selling the business back pays this share of what was paid for it
  resale: z.number().min(0).max(1),
  // tax on the week's takings, taken with the bills
  tax: z.number().min(0).max(0.9),
  // all of one player's businesses together take at most this in an evening
  nightCapPence: z.number().int().min(100).max(1_000_000_000),
  // days of grace for unpaid rent before the doors shut
  graceDays: z.number().int().min(0).max(14),
  // evenings missed while nobody looked are caught up, up to this many
  catchUp: z.number().int().min(1).max(14),
});
export type BusinessRules = z.infer<typeof BusinessRules>;

export const BusinessContent = z.object({
  kinds: z.array(BusinessKind).min(1).max(30),
  premises: z.object({ stall: Premises, shop: Premises, large: Premises }),
  areas: z.array(BusinessArea).min(1).max(30),
  tiers: z.object({ '1': AreaTier, '2': AreaTier, '3': AreaTier, '4': AreaTier }),
  rules: BusinessRules,
  // the shelf: what players' shops sell other players (content saved before shelves uses the defaults)
  products: z.array(ShopProduct).max(100).optional(),
  shelf: ShelfRules.optional(),
});
export type BusinessContent = z.infer<typeof BusinessContent>;

const week = (...days: number[]) => days;
export const DEFAULT_BUSINESS: BusinessContent = {
  kinds: [
    {
      id: 'food_stall',
      label: 'Food stall',
      icon: '🥙',
      blurb: 'Wraps, jerk chicken, jollof. Cheap to start',
      setupPence: 150_000,
      ticketPence: 800,
      margin: 0.35,
      customers: 30,
      staff: { min: 1, usual: 1, max: 3 },
      wagePence: 9_000,
      rentShare: 0.06,
      days: week(0.9, 0.9, 1, 1, 1.15, 1.3, 1),
      swing: [0.8, 1.2],
      visible: true,
    },
    {
      id: 'cafe',
      label: 'Café',
      icon: '☕',
      blurb: 'Flat whites and pastries. Busy every day',
      setupPence: 300_000,
      ticketPence: 550,
      margin: 0.6,
      customers: 40,
      staff: { min: 1, usual: 2, max: 5 },
      wagePence: 10_000,
      rentShare: 0.055,
      days: week(1, 1, 1, 1, 1.05, 1.2, 1.1),
      swing: [0.85, 1.15],
      visible: true,
    },
    {
      id: 'barber',
      label: 'Barber & salon',
      icon: '💈',
      blurb: 'Fades, braids, nails. Saturday is big',
      setupPence: 400_000,
      ticketPence: 1_800,
      margin: 0.55,
      customers: 12,
      staff: { min: 1, usual: 3, max: 6 },
      wagePence: 12_000,
      rentShare: 0.05,
      days: week(0.8, 0.9, 0.9, 1, 1.1, 1.5, 0.8),
      swing: [0.8, 1.2],
      visible: true,
    },
    {
      id: 'corner_shop',
      label: 'Corner shop',
      icon: '🛒',
      blurb: 'Milk, bread, phone top-ups. Steady money',
      setupPence: 800_000,
      ticketPence: 900,
      margin: 0.2,
      customers: 90,
      staff: { min: 1, usual: 3, max: 8 },
      wagePence: 11_000,
      rentShare: 0.045,
      days: week(1, 1, 1, 1, 1, 1.1, 1.05),
      swing: [0.9, 1.1],
      visible: true,
    },
    {
      id: 'boutique',
      label: 'Fashion boutique',
      icon: '👗',
      blurb: 'Trainers, jackets, bags. Swingy evenings',
      setupPence: 1_500_000,
      ticketPence: 4_500,
      margin: 0.45,
      customers: 10,
      staff: { min: 1, usual: 3, max: 8 },
      wagePence: 13_000,
      rentShare: 0.04,
      days: week(0.9, 0.9, 0.9, 1, 1.2, 1.4, 1.1),
      swing: [0.6, 1.4],
      visible: true,
    },
    {
      id: 'pharmacy',
      label: 'Pharmacy',
      icon: '💊',
      blurb: 'Prescriptions and plasters. Rarely a bad night',
      setupPence: 3_000_000,
      ticketPence: 2_200,
      margin: 0.3,
      customers: 45,
      staff: { min: 2, usual: 4, max: 10 },
      wagePence: 20_000,
      rentShare: 0.04,
      days: week(1.05, 1, 1, 1, 1, 0.95, 0.8),
      swing: [0.9, 1.1],
      visible: true,
    },
  ],
  premises: {
    stall: { label: 'Stall', icon: '🛖', customers: 1, rent: 1, setup: 1, staff: 1, capPence: 40_000, stock: 60 },
    shop: { label: 'Shop', icon: '🏪', customers: 3, rent: 2.2, setup: 3, staff: 2, capPence: 200_000, stock: 250 },
    large: {
      label: 'Big store',
      icon: '🏬',
      customers: 8,
      rent: 5,
      setup: 9,
      staff: 5,
      capPence: 1_000_000,
      stock: 1_000,
    },
  },
  areas: [
    { id: 'deptford', name: 'Deptford', tier: 1 },
    { id: 'woolwich', name: 'Woolwich', tier: 1 },
    { id: 'charlton', name: 'Charlton', tier: 1 },
    { id: 'poplar', name: 'Poplar', tier: 2 },
    { id: 'silvertown', name: 'Silvertown', tier: 2 },
    { id: 'royal_docks', name: 'Royal Docks', tier: 2 },
    { id: 'greenwich', name: 'Greenwich', tier: 3 },
    { id: 'isle_of_dogs', name: 'Isle of Dogs', tier: 3 },
    { id: 'peninsula', name: 'Greenwich Peninsula', tier: 3 },
    { id: 'canary_wharf', name: 'Canary Wharf', tier: 4 },
  ],
  tiers: {
    '1': { rent: 0.6, setup: 0.6, customers: 0.8, ticket: 0.8, wage: 0.8 },
    '2': { rent: 1, setup: 1, customers: 1, ticket: 1, wage: 1 },
    '3': { rent: 1.8, setup: 1.8, customers: 1.2, ticket: 1.4, wage: 1.2 },
    '4': { rent: 3, setup: 3, customers: 1.3, ticket: 2, wage: 1.5 },
  },
  rules: {
    open: true,
    registerPence: 5_000,
    maxBusinesses: 3,
    buyWeeks: 40,
    resale: 0.6,
    tax: 0.05,
    nightCapPence: 2_000_000,
    graceDays: 3,
    catchUp: 3,
  },
  products: DEFAULT_PRODUCTS,
  shelf: DEFAULT_SHELF_RULES,
};

// mistakes the editor points out before publishing
export function businessProblems(content: BusinessContent): string[] {
  const problems: string[] = [];
  const ids = new Set<string>();
  for (const kind of content.kinds) {
    if (ids.has(kind.id)) problems.push(`Two businesses have the id "${kind.id}".`);
    ids.add(kind.id);
    if (!(kind.staff.min <= kind.staff.usual && kind.staff.usual <= kind.staff.max))
      problems.push(`${kind.label}: the staff numbers must go fewest ≤ usual ≤ most.`);
    if (kind.swing[0] > kind.swing[1]) problems.push(`${kind.label}: the swing goes low to high.`);
  }
  const products = new Set<string>();
  for (const item of content.products ?? []) {
    if (products.has(item.id)) problems.push(`Two products have the id "${item.id}".`);
    products.add(item.id);
    for (const kind of item.kinds)
      if (!ids.has(kind)) problems.push(`${item.label} is sold by a kind of business that does not exist ("${kind}").`);
  }
  const areas = new Set<string>();
  for (const area of content.areas) {
    if (areas.has(area.id)) problems.push(`Two areas have the id "${area.id}".`);
    areas.add(area.id);
  }
  return problems;
}

// ---- the arithmetic (the API and the apps use the same)

const round = (pence: number, to = 100) => Math.max(0, Math.round(pence / to) * to);
export type BusinessPlan = { kind: BusinessKind; area: BusinessArea; size: PremisesKey };
const tierOf = (content: BusinessContent, area: BusinessArea) => content.tiers[String(area.tier) as '1'];

// fitting it out
export const setupPence = (content: BusinessContent, plan: BusinessPlan) =>
  round(plan.kind.setupPence * content.premises[plan.size].setup * tierOf(content, plan.area).setup);
// a week's rent of the premises
export const weeklyRentPence = (content: BusinessContent, plan: BusinessPlan) =>
  round(
    plan.kind.setupPence * plan.kind.rentShare * content.premises[plan.size].rent * tierOf(content, plan.area).rent,
  );
// buying the premises instead of renting them
export const buyPricePence = (content: BusinessContent, plan: BusinessPlan) =>
  weeklyRentPence(content, plan) * content.rules.buyWeeks;
// a week's wage for one person working there
export const wagePence = (content: BusinessContent, plan: BusinessPlan) =>
  round(plan.kind.wagePence * tierOf(content, plan.area).wage);
// how many can work there
export const staffRange = (content: BusinessContent, plan: BusinessPlan) => {
  const scale = content.premises[plan.size].staff;
  return {
    min: Math.round(plan.kind.staff.min * scale),
    usual: Math.max(1, Math.round(plan.kind.staff.usual * scale)),
    max: Math.max(1, Math.round(plan.kind.staff.max * scale)),
  };
};
// what the staff do for trade: short-handed loses customers, extra hands add a little
export const staffEffect = (staff: number, range: { usual: number; max: number }) =>
  Math.min(1, staff / range.usual) + 0.05 * Math.max(0, Math.min(staff, range.max) - range.usual);

// one evening's trade (`luck` is 0 to 1: how the evening's swing fell; 0.5 is an ordinary evening)
export function eveningTrade(
  content: BusinessContent,
  plan: BusinessPlan & { staff: number; price: PriceKey },
  weekday: number,
  luck: number,
) {
  const premises = content.premises[plan.size];
  const tier = tierOf(content, plan.area);
  const price = PRICE_CHOICES[plan.price];
  const swing = plan.kind.swing[0] + (plan.kind.swing[1] - plan.kind.swing[0]) * Math.max(0, Math.min(1, luck));
  const customers = Math.max(
    0,
    Math.round(
      plan.kind.customers *
        premises.customers *
        tier.customers *
        price.customers *
        (plan.kind.days[((weekday % 7) + 7) % 7] ?? 1) *
        staffEffect(plan.staff, staffRange(content, plan)) *
        swing,
    ),
  );
  const spent = customers * plan.kind.ticketPence * tier.ticket * price.ticket;
  const takingsPence = Math.min(premises.capPence, Math.round(spent * plan.kind.margin));
  return { customers, takingsPence };
}
// an ordinary evening and week, for the sums shown before starting one
export function businessEstimate(content: BusinessContent, plan: BusinessPlan & { staff?: number; price?: PriceKey }) {
  const range = staffRange(content, plan);
  const staff = plan.staff ?? range.usual;
  const full = { ...plan, staff, price: plan.price ?? 'fair' };
  const nights = [0, 1, 2, 3, 4, 5, 6].map((day) => eveningTrade(content, full, day, 0.5).takingsPence);
  const weekTakings = nights.reduce((sum, night) => sum + night, 0);
  const wages = staff * wagePence(content, plan);
  return {
    nightPence: Math.round(weekTakings / 7),
    weekTakingsPence: weekTakings,
    weekWagesPence: wages,
    weekRentPence: weeklyRentPence(content, plan),
    weekTaxPence: Math.round(weekTakings * content.rules.tax),
    staff: range,
  };
}
// a fixed "luck" for one business on one evening: the same evening always comes out the same
export function eveningLuck(businessId: string, day: string) {
  let hash = 0x811c9dc5;
  for (const char of `${businessId}:${day}`) hash = Math.imul(hash ^ char.charCodeAt(0), 0x1000193);
  let seed = hash >>> 0;
  seed = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  seed ^= seed + Math.imul(seed ^ (seed >>> 7), 61 | seed);
  return ((seed ^ (seed >>> 14)) >>> 0) / 4_294_967_296;
}

// ---- what the apps see

export const BusinessStatus = z.enum(['open', 'shut', 'sold', 'closed']);
export type BusinessStatus = z.infer<typeof BusinessStatus>;
export const Tenure = z.enum(['rent', 'own']);
export type Tenure = z.infer<typeof Tenure>;

export const BusinessEvent = z.object({
  at: z.string(),
  kind: z.enum([
    'stocked',
    'shelf_sale',
    'started',
    'takings',
    'bills',
    'cash_out',
    'staff',
    'staff_left',
    'price',
    'shut',
    'reopened',
    'sold',
    'closed',
  ]),
  amountPence: z.number().int(),
  text: z.string(),
});
export type BusinessEvent = z.infer<typeof BusinessEvent>;

// a product on a shelf
export const ShelfItemView = z.object({
  productId: z.string(),
  label: z.string(),
  icon: z.string(),
  pricePence: z.number().int(),
  stock: z.number().int(),
  listed: z.boolean(),
  // what one more in stock costs the owner (owners only)
  costPence: z.number().int().optional(),
  effects: z.record(z.string(), z.number()),
  // a look the buyer takes on (one at a time)
  wear: z.object({ part: z.enum(WEAR_PARTS), value: z.string() }).optional(),
});
export type ShelfItemView = z.infer<typeof ShelfItemView>;
export const ShelfView = z.object({
  open: z.boolean(),
  // the storeroom: what it holds, and what is in it
  room: z.number().int(),
  stocked: z.number().int(),
  items: z.array(ShelfItemView),
  soldTodayPence: z.number().int(),
});
export type ShelfView = z.infer<typeof ShelfView>;

export const BusinessView = z.object({
  id: z.string().uuid(),
  name: z.string(),
  kind: z.string(),
  area: z.string(),
  size: z.enum(PREMISES_KEYS),
  tenure: Tenure,
  status: BusinessStatus,
  staff: z.number().int(),
  price: z.enum(PRICE_KEYS),
  tillPence: z.number().int(),
  weeklyRentPence: z.number().int(),
  // a week's wage for one of the staff
  wagePence: z.number().int(),
  // what the next bills will come to (rent, wages, tax so far this week)
  billsPence: z.number().int(),
  weekTakingsPence: z.number().int(),
  owedPence: z.number().int(),
  graceUntil: z.string().nullable(),
  lastEvening: z.object({ at: z.string(), customers: z.number().int(), takingsPence: z.number().int() }).nullable(),
  nextEveningAt: z.string(),
  nextBillsAt: z.string(),
  // what selling it back pays now (and the till comes too)
  salePence: z.number().int(),
  events: z.array(BusinessEvent),
  // its shelf for other players (null: this kind of business sells nothing to players)
  shelf: ShelfView.nullable(),
  createdAt: z.string(),
});
export type BusinessView = z.infer<typeof BusinessView>;
export const BusinessesReply = z.object({ businesses: z.array(BusinessView), walletPence: z.number().int() });
export type BusinessesReply = z.infer<typeof BusinessesReply>;

export const StartBusinessRequest = z.object({
  name: z.string().trim().min(3, 'Give it a name of 3 letters or more').max(30),
  kind: z.string().max(30),
  area: z.string().max(30),
  size: z.enum(PREMISES_KEYS),
  tenure: Tenure,
});
export type StartBusinessRequest = z.infer<typeof StartBusinessRequest>;
export const BusinessAction = z.discriminatedUnion('action', [
  z.object({ action: z.literal('staff'), staff: z.number().int().min(0).max(1000) }),
  z.object({ action: z.literal('price'), price: z.enum(PRICE_KEYS) }),
  // the till to the wallet (all of it when no amount is given)
  z.object({ action: z.literal('cash_out'), amountPence: z.number().int().positive().optional() }),
  // pay rent owed from the wallet (the doors open again)
  z.object({ action: z.literal('pay_owed') }),
  z.object({ action: z.literal('sell'), confirm: z.literal(true) }),
  // the shelf: what is on it and at what price (stock stays); stocking it (from the till or the wallet); open or not
  z.object({
    action: z.literal('shelf'),
    items: z
      .array(z.object({ productId: z.string().max(30), pricePence: z.number().int().positive(), listed: z.boolean() }))
      .max(40),
  }),
  z.object({
    action: z.literal('restock'),
    productId: z.string().max(30),
    qty: z.number().int().min(1).max(100_000),
    from: z.enum(['till', 'wallet']),
  }),
  z.object({ action: z.literal('shelf_open'), open: z.boolean() }),
]);
export type BusinessAction = z.infer<typeof BusinessAction>;
export const BusinessActionReply = BusinessesReply.extend({ message: z.string() });
export type BusinessActionReply = z.infer<typeof BusinessActionReply>;

// ---- players' shops, for those buying
export const ShopView = z.object({
  businessId: z.string().uuid(),
  name: z.string(),
  kind: z.string(),
  area: z.string(),
  owner: z.object({ lifeId: z.string().uuid(), username: z.string().nullable(), firstName: z.string() }),
  mine: z.boolean(),
  items: z.array(ShelfItemView),
});
export type ShopView = z.infer<typeof ShopView>;
export const ShopsReply = z.object({
  shops: z.array(ShopView),
  walletPence: z.number().int(),
  // why this player cannot buy yet (a new life waits a few days), or null
  waitUntil: z.string().nullable(),
});
export type ShopsReply = z.infer<typeof ShopsReply>;
export const ShopBuyRequest = z.object({ productId: z.string().max(30), qty: z.number().int().min(1).max(50) });
export type ShopBuyRequest = z.infer<typeof ShopBuyRequest>;
export const ShopBuyReply = ShopsReply.extend({ message: z.string() });
export type ShopBuyReply = z.infer<typeof ShopBuyReply>;
